export interface Ingredient {
  uuid: string
  inciName: string
  commonName?: string
  casNumber?: string
  ecNumber?: string
  hasEssentialOils: boolean
  hasAllergens: boolean
  hasEcoToxic: boolean
  hasEndocrineDisruptors: boolean
  toValidate: boolean
  cir?: string
  functions?: string[]
  description?: string
  createdAt?: string
  updatedAt?: string
}

export enum Rating {
  A = 'a',
  B = 'b',
  C = 'c'
}

export interface RatingPair {
  jsonKey: string
  rating: Rating | null
}

export interface IngredientDetails extends Ingredient {
  skinTypes: RatingPair[]
  skinConditions: RatingPair[]
  specificities: RatingPair[]
  allergies: RatingPair[]
  hairTypes: RatingPair[]
  hairConditions: RatingPair[]
  eyes: RatingPair[]
  lipTypes: RatingPair[]
  nails: RatingPair[]
  comments?: string
}

export const RATING_CATEGORIES = {
  skinTypes: ['dry', 'normal', 'oily', 'combination'],
  skinConditions: ['acneProne', 'sensitive', 'dehydrated', 'mature', 'rosacea', 'dull', 'atopic'],
  specificities: ['pregnant', 'nursing', 'postTherapeutic', 'children'],
  allergies: [
    'food',
    'respiratory',
    'medication',
    'skin',
    'animals',
    'fragrance',
    'plants',
    'preservatives',
    'dyes',
    'photosensitivity',
  ],
  hairTypes: ['straight', 'wavy', 'curly', 'coily'],
  hairConditions: ['normal', 'dry', 'oily', 'combination'],
  eyes: ['reactiveEyelids', 'contactLenses', 'sensitiveEyes'],
  lipTypes: ['normal', 'dry'],
  nails: ['fragile', 'normal'],
}

export const RATING_TRANSLATIONS: Record<string, Record<string, string>> = {
  skinTypes: {
    dry: 'Sèche',
    normal: 'Normale',
    oily: 'Grasse',
    combination: 'Mixte',
  },
  skinConditions: {
    acneProne: 'Tendance acnéique',
    sensitive: 'Sensible',
    dehydrated: 'Déshydratée',
    mature: 'Mature',
    rosacea: 'Couperosée',
    dull: 'Atone',
    atopic: 'Atopique',
  },
  specificities: {
    pregnant: 'Enceinte',
    nursing: 'Allaitante',
    postTherapeutic: 'Post-thérapeutique',
    children: 'Enfants',
  },
  allergies: {
    food: 'Alimentaires',
    respiratory: 'Respiratoires',
    medication: 'Médicamenteuses',
    skin: 'Cutanées',
    animals: 'Animaux',
    fragrance: 'Parfums',
    plants: 'Plantes',
    preservatives: 'Conservateurs',
    dyes: 'Colorants',
    photosensitivity: 'Photosensibilité',
  },
  hairTypes: {
    straight: 'Raides',
    wavy: 'Ondulés',
    curly: 'Bouclés, frisés',
    coily: 'Crépus',
  },
  hairConditions: {
    normal: 'Normaux',
    dry: 'Secs',
    oily: 'Gras',
    combination: 'Mixtes',
  },
  eyes: {
    reactiveEyelids: 'Paupières réactives',
    contactLenses: 'Porteur de lentilles',
    sensitiveEyes: 'Yeux sensibles',
  },
  lipTypes: {
    normal: 'Normales',
    dry: 'Sèches',
  },
  nails: {
    fragile: 'Fragiles',
    normal: 'Normaux',
  },
}

export interface IngredientsResponse {
  data: Ingredient[]
  currentPage: number
  lastPage: number
  pageSize: number
  totalRecords: number
  from: number
  to: number
}

export interface IngredientsFilters {
  query?: string
  toValidate?: boolean | null
  page?: number
  limit?: number
  sortBy?: string
}